import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { AlertCircle, Building2, UserCircle, Trophy } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { toDisplayDate } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { AddressAutocomplete, type AddressDetails } from '@/components/ui/address-autocomplete'
import { Card, CardHeader, CardContent } from '@/components/ui/card'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { useToast } from '@/hooks/use-toast'
import {
  useConvertLead,
  useAccounts,
  useContacts,
  useOpportunities,
  useUsers,
} from '@/hooks/use-api'
import { useDebounce } from '@/hooks/use-debounce'
import { usePicklistOptions } from '@/hooks/use-picklists'
import type { Lead, Account, Contact, Opportunity } from '@/types'
import { CATEGORICAL } from '@/lib/theme'
import { explainError } from '@/lib/explain-error'

interface ConversionResult {
  lead: Lead
  contact: Contact
  account: Account | null
  opportunity: Opportunity | null
}

interface ConvertLeadModalProps {
  lead: Lead | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ConvertLeadModal({ lead, open, onOpenChange }: ConvertLeadModalProps) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const convertMutation = useConvertLead()

  const opportunityStages = usePicklistOptions('opportunity', 'stage_name')
  const convertedStatuses = usePicklistOptions('lead', 'converted_status')

  // Server-side search for dropdowns
  const [accountSearch, setAccountSearch] = useState('')
  const [opportunitySearch, setOpportunitySearch] = useState('')
  const debouncedAccountSearch = useDebounce(accountSearch, 300)
  const debouncedOpportunitySearch = useDebounce(opportunitySearch, 300)

  // Data hooks with server-side search
  const { data: accountsData, isFetching: accountsLoading } = useAccounts({
    search: debouncedAccountSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })
  const { data: usersData } = useUsers({ limit: 10000, sort_by: 'name', sort_order: 'asc' })
  const { data: opportunitiesData, isFetching: opportunitiesLoading } = useOpportunities({
    search: debouncedOpportunitySearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })

  // Match detection hooks - only fire when modal is open
  const { data: matchedAccountsData } = useAccounts({
    search: lead?.company || '',
    limit: 5,
    sort_by: 'name',
    sort_order: 'asc',
  })
  // Lead conversion: only suggest Active contacts to link to. Inactive contacts
  // (people who've left) shouldn't be associated with new opportunities. If a
  // user needs to manually link an inactive contact, they can do so from the
  // contact's record after conversion. KNOWN TRADEOFF (review MEDIUM-3): may
  // miss a duplicate match against an inactive person with the same email.
  const { data: matchedContactsData } = useContacts({
    search: lead?.email || lead?.last_name || '',
    limit: 5,
    status__c: 'Active',
  })

  const accounts = accountsData?.data ?? []
  const users = usersData?.data ?? []
  const opportunities = opportunitiesData?.data ?? []
  const matchedAccounts = open && lead?.company ? (matchedAccountsData?.data ?? []) : []
  const matchedContacts = open && lead ? (matchedContactsData?.data ?? []) : []
  const firstMatchedAccount = matchedAccounts[0] ?? null
  const firstMatchedContact = matchedContacts[0] ?? null

  // Form state
  const [accountMode, setAccountMode] = useState<'create' | 'existing'>('create')
  const [accountName, setAccountName] = useState('')
  const [accountId, setAccountId] = useState('')
  const [accountPhone, setAccountPhone] = useState('')
  const [accountBillingStreet, setAccountBillingStreet] = useState('')
  const [accountBillingCity, setAccountBillingCity] = useState('')
  const [accountBillingState, setAccountBillingState] = useState('')
  const [accountBillingPostalCode, setAccountBillingPostalCode] = useState('')
  const [accountBillingCountry, setAccountBillingCountry] = useState('')

  const [contactMode, setContactMode] = useState<'create' | 'existing'>('create')
  const [contactId, setContactId] = useState('')

  const [opportunityMode, setOpportunityMode] = useState<'create' | 'existing' | 'skip'>('create')
  const [opportunityId, setOpportunityId] = useState('')
  const [opportunityName, setOpportunityName] = useState('')
  const [opportunityAmount, setOpportunityAmount] = useState('')
  const [opportunityCloseDate, setOpportunityCloseDate] = useState('')
  const [opportunityStage, setOpportunityStage] = useState('Prospecting')
  const [skipOpportunity, setSkipOpportunity] = useState(false)

  const [ownerId, setOwnerId] = useState('')
  const [convertedStatus, setConvertedStatus] = useState('Qualified - Converted')

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Post-conversion success state
  const [conversionResult, setConversionResult] = useState<ConversionResult | null>(null)
  const [taskModalOpen, setTaskModalOpen] = useState(false)

  // Reset form when modal opens with new lead
  useEffect(() => {
    if (open && lead) {
      // If the lead was domain-matched to an existing account at intake, default to
      // linking that account; otherwise default to creating a new one.
      const hasMatchedAccount = !!lead.matched_account_id
      setAccountMode(hasMatchedAccount ? 'existing' : 'create')
      setAccountName(lead.company || '')
      setAccountId(hasMatchedAccount ? lead.matched_account_id! : '')
      setAccountPhone(lead.phone || '')
      setAccountBillingStreet(lead.street || '')
      setAccountBillingCity(lead.city || '')
      setAccountBillingState(lead.state || '')
      setAccountBillingPostalCode(lead.postal_code || '')
      setAccountBillingCountry(lead.country || '')
      setContactMode('create')
      setContactId('')
      setOpportunityMode('create')
      setOpportunityId('')
      setOpportunityName(lead.company ? `${lead.company} - Opportunity` : '')
      setOpportunityAmount('')
      setOpportunityCloseDate('')
      setOpportunityStage('Prospecting')
      setSkipOpportunity(false)
      setOwnerId(lead.owner_id || '')
      setConvertedStatus('Qualified - Converted')
      setErrors({})
      setConversionResult(null)
    }
  }, [open, lead])

  // Sync skipOpportunity with opportunityMode
  useEffect(() => {
    if (skipOpportunity) {
      setOpportunityMode('skip')
    } else if (opportunityMode === 'skip') {
      setOpportunityMode('create')
    }
  }, [skipOpportunity, opportunityMode])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Owner validation
    if (!ownerId) {
      newErrors.ownerId = 'Record owner is required'
    }

    // Account validation
    if (accountMode === 'existing' && !accountId) {
      newErrors.accountId = 'Please select an existing account'
    }
    if (accountMode === 'create') {
      if (!accountName?.trim()) {
        newErrors.accountName = 'Account name is required when creating a new account'
      }
      if (!accountPhone?.trim()) {
        newErrors.accountPhone = 'Phone number is required'
      }
      if (!accountBillingStreet?.trim()) {
        newErrors.accountBillingStreet = 'Street address is required'
      }
      if (!accountBillingCity?.trim()) {
        newErrors.accountBillingCity = 'City is required'
      }
      if (!accountBillingState?.trim()) {
        newErrors.accountBillingState = 'State/Province is required'
      }
      if (!accountBillingPostalCode?.trim()) {
        newErrors.accountBillingPostalCode = 'Postal code is required'
      }
      if (!accountBillingCountry?.trim()) {
        newErrors.accountBillingCountry = 'Country is required'
      }
    }

    // Contact validation
    if (contactMode === 'existing' && !contactId) {
      newErrors.contactId = 'Please select an existing contact'
    }

    // Opportunity validation
    if (opportunityMode === 'create') {
      if (!opportunityName?.trim()) {
        newErrors.opportunityName = 'Opportunity name is required'
      }
      if (!opportunityCloseDate) {
        newErrors.opportunityCloseDate = 'Close date is required'
      } else {
        const date = new Date(opportunityCloseDate)
        if (isNaN(date.getTime())) {
          newErrors.opportunityCloseDate = 'Invalid date format'
        }
      }
      if (opportunityAmount && isNaN(parseFloat(opportunityAmount))) {
        newErrors.opportunityAmount = 'Amount must be a valid number'
      }
    }
    if (opportunityMode === 'existing' && !opportunityId) {
      newErrors.opportunityId = 'Please select an existing opportunity'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!lead) return

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await convertMutation.mutateAsync({
        id: lead.id,
        options: {
          // Account
          createAccount: accountMode === 'create',
          accountName: accountMode === 'create' ? accountName : undefined,
          accountId: accountMode === 'existing' ? accountId : undefined,
          accountPhone: accountMode === 'create' ? accountPhone : undefined,
          accountBillingStreet: accountMode === 'create' ? accountBillingStreet : undefined,
          accountBillingCity: accountMode === 'create' ? accountBillingCity : undefined,
          accountBillingState: accountMode === 'create' ? accountBillingState : undefined,
          accountBillingPostalCode: accountMode === 'create' ? accountBillingPostalCode : undefined,
          accountBillingCountry: accountMode === 'create' ? accountBillingCountry : undefined,
          // Contact
          createContact: contactMode === 'create',
          contactId: contactMode === 'existing' ? contactId : undefined,
          // Opportunity
          createOpportunity: opportunityMode !== 'skip',
          opportunityId: opportunityMode === 'existing' ? opportunityId : undefined,
          opportunityName: opportunityMode === 'create' ? opportunityName : undefined,
          opportunityAmount:
            opportunityMode === 'create' && opportunityAmount
              ? parseFloat(opportunityAmount)
              : undefined,
          opportunityCloseDate:
            opportunityMode === 'create' && opportunityCloseDate ? opportunityCloseDate : undefined,
          opportunityStage: opportunityMode === 'create' ? opportunityStage : undefined,
          // New fields
          ownerId,
          convertedStatus,
        },
      })

      setConversionResult(result as ConversionResult)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to convert lead')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const clearError = (field: string) => {
    if (errors[field]) {
      const newErrors = { ...errors }
      delete newErrors[field]
      setErrors(newErrors)
    }
  }

  const handleGoToLeads = () => {
    onOpenChange(false)
    navigate('/leads')
  }

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount == null) return ''
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const formatDate = (date: string | undefined | null) => {
    if (!date) return ''
    try {
      const d = toDisplayDate(date)
      if (!d) return date
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return date
    }
  }

  const truncateUrl = (url: string | undefined | null, maxLen = 25) => {
    if (!url) return ''
    return url.length > maxLen ? url.substring(0, maxLen) + '...' : url
  }

  if (!lead) return null

  // Success view after conversion
  if (conversionResult) {
    const {
      contact: convertedContact,
      account: convertedAccount,
      opportunity: convertedOpp,
    } = conversionResult

    return (
      <Dialog
        open={open}
        onOpenChange={() => {
          /* prevent closing via overlay/escape — user must click a button */
        }}
      >
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          aria-describedby="conversion-success-description"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="sr-only" id="conversion-success-description">
            Lead conversion results showing the created account, contact, and opportunity records.
          </div>

          <div className="text-center space-y-4 pt-2" role="status" aria-live="polite">
            <h2 className="text-2xl font-semibold">Your lead has been converted</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {/* Account Card */}
            {convertedAccount && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Building2
                      className="h-5 w-5 flex-shrink-0"
                      style={{ color: CATEGORICAL.navy.solid }}
                      aria-hidden="true"
                    />
                    <Link
                      to={`/accounts/${convertedAccount.id}`}
                      className="text-lg font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                      style={{ color: CATEGORICAL.navy.solid }}
                      onClick={() => onOpenChange(false)}
                    >
                      {convertedAccount.name || convertedAccount.account_name}
                    </Link>
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-1">
                    Account
                  </p>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  <div>
                    <span className="text-muted-foreground">Type:</span>{' '}
                    {convertedAccount.account_type || convertedAccount.type || ''}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Phone:</span>{' '}
                    {convertedAccount.phone || ''}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Website:</span>{' '}
                    {convertedAccount.website ? (
                      <a
                        href={
                          convertedAccount.website.startsWith('http')
                            ? convertedAccount.website
                            : `https://${convertedAccount.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                        style={{ color: CATEGORICAL.navy.solid }}
                      >
                        {truncateUrl(convertedAccount.website)}
                      </a>
                    ) : (
                      ''
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Account Owner:</span>{' '}
                    <span>{convertedAccount.owner_name || ''}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Industry:</span>{' '}
                    {convertedAccount.industry || ''}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Card */}
            {convertedContact && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <UserCircle
                      className="h-5 w-5 flex-shrink-0"
                      style={{ color: CATEGORICAL.evergreen.solid }}
                      aria-hidden="true"
                    />
                    <Link
                      to={`/contacts/${convertedContact.id}`}
                      className="text-lg font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                      style={{ color: CATEGORICAL.navy.solid }}
                      onClick={() => onOpenChange(false)}
                    >
                      {convertedContact.first_name} {convertedContact.last_name}
                    </Link>
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-1">
                    Contact
                  </p>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  <div>
                    <span className="text-muted-foreground">Title:</span>{' '}
                    {convertedContact.title || ''}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Account Name:</span>{' '}
                    {convertedAccount ? (
                      <Link
                        to={`/accounts/${convertedAccount.id}`}
                        className="hover:underline"
                        style={{ color: CATEGORICAL.navy.solid }}
                        onClick={() => onOpenChange(false)}
                      >
                        {convertedAccount.name || convertedAccount.account_name}
                      </Link>
                    ) : (
                      convertedContact.account_name || ''
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Phone:</span>{' '}
                    {convertedContact.phone || ''}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>{' '}
                    {convertedContact.email ? (
                      <a
                        href={`mailto:${convertedContact.email}`}
                        className="hover:underline"
                        style={{ color: CATEGORICAL.navy.solid }}
                      >
                        {convertedContact.email}
                      </a>
                    ) : (
                      ''
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Mobile:</span>{' '}
                    {convertedContact.mobile || convertedContact.mobile_phone || ''}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Opportunity Card */}
            {convertedOpp && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Trophy
                      className="h-5 w-5 flex-shrink-0"
                      style={{ color: CATEGORICAL.clay.solid }}
                      aria-hidden="true"
                    />
                    <Link
                      to={`/opportunities/${convertedOpp.id}`}
                      className="text-lg font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                      style={{ color: CATEGORICAL.navy.solid }}
                      onClick={() => onOpenChange(false)}
                    >
                      {convertedOpp.name || convertedOpp.opportunity_name}
                    </Link>
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-1">
                    Opportunity
                  </p>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  <div>
                    <span className="text-muted-foreground">Account Name:</span>{' '}
                    {convertedAccount ? (
                      <Link
                        to={`/accounts/${convertedAccount.id}`}
                        className="hover:underline"
                        style={{ color: CATEGORICAL.navy.solid }}
                        onClick={() => onOpenChange(false)}
                      >
                        {convertedAccount.name || convertedAccount.account_name}
                      </Link>
                    ) : (
                      convertedOpp.account_name || ''
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Close Date:</span>{' '}
                    {formatDate(convertedOpp.close_date)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Amount:</span>{' '}
                    {convertedOpp.amount ? formatCurrency(convertedOpp.amount) : ''}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Opportunity Owner:</span>{' '}
                    {convertedOpp.owner_name || ''}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t">
            <Button type="button" variant="outline" onClick={() => setTaskModalOpen(true)}>
              New Task
            </Button>
            <Button type="button" onClick={handleGoToLeads}>
              Go to Leads
            </Button>
          </div>

          {/* Create Task Modal */}
          <CreateTaskModal
            open={taskModalOpen}
            onOpenChange={setTaskModalOpen}
            relatedToId={convertedContact?.id}
            relatedToType="Contact"
            relatedToName={
              convertedContact
                ? `${convertedContact.first_name} ${convertedContact.last_name}`
                : undefined
            }
            ownerId={convertedContact?.owner_id}
          />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        aria-describedby="convert-lead-description"
      >
        <DialogHeader>
          <DialogTitle>
            Convert Lead: {lead.first_name} {lead.last_name}
          </DialogTitle>
          <DialogDescription id="convert-lead-description">
            Convert this lead to a contact, account, and optionally an opportunity.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Lead Information */}
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <h3 className="font-semibold text-sm bg-muted px-3 py-1.5 rounded-sm">
              Lead Information
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Name:</span>{' '}
                <span className="font-medium">
                  {lead.first_name} {lead.last_name}
                </span>
              </div>
              {lead.email && (
                <div>
                  <span className="text-muted-foreground">Email:</span>{' '}
                  <span className="font-medium">{lead.email}</span>
                </div>
              )}
              {lead.company && (
                <div>
                  <span className="text-muted-foreground">Company:</span>{' '}
                  <span className="font-medium">{lead.company}</span>
                </div>
              )}
              {lead.title && (
                <div>
                  <span className="text-muted-foreground">Title:</span>{' '}
                  <span className="font-medium">{lead.title}</span>
                </div>
              )}
            </div>
          </div>

          {/* Record Owner */}
          <fieldset className="space-y-3 border-t pt-4">
            <legend className="font-semibold text-base">Record Owner</legend>
            <RecordCombobox
              id="record-owner"
              records={users}
              value={ownerId}
              onValueChange={(val) => {
                setOwnerId(val)
                clearError('ownerId')
              }}
              placeholder="Select record owner"
              searchPlaceholder="Search users..."
              emptyMessage="No users found."
              displayField={(u) => u.name || ''}
              aria-label="Record owner"
              totalCount={usersData?.total}
            />
            {errors.ownerId && (
              <p id="owner-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.ownerId}
              </p>
            )}
          </fieldset>

          {/* Converted Status */}
          <fieldset className="space-y-3 border-t pt-4">
            <legend className="font-semibold text-base">Converted Status</legend>
            <Select value={convertedStatus} onValueChange={setConvertedStatus}>
              <SelectTrigger id="converted-status" aria-label="Converted status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {convertedStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </fieldset>

          {/* Account Section */}
          <fieldset className="space-y-4 border-t pt-4">
            <legend className="font-semibold text-base">Account</legend>

            {/* Match detection alert */}
            {accountMode === 'create' && firstMatchedAccount && (
              <Alert variant="warning" aria-live="polite">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>Existing Account Found</AlertTitle>
                <AlertDescription>
                  An existing account &ldquo;
                  {firstMatchedAccount.name || firstMatchedAccount.account_name}&rdquo; was found
                  matching this lead&apos;s company.{' '}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-sm"
                    onClick={() => {
                      setAccountMode('existing')
                      setAccountId(firstMatchedAccount.id)
                      clearError('accountName')
                    }}
                  >
                    Use this account
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <RadioGroup
              value={accountMode}
              onValueChange={(val) => {
                setAccountMode(val as 'create' | 'existing')
                clearError('accountId')
                clearError('accountName')
              }}
              aria-label="Account creation mode"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="create" id="account-create" />
                <Label htmlFor="account-create" className="cursor-pointer font-normal">
                  Create New
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="existing" id="account-existing" />
                <Label htmlFor="account-existing" className="cursor-pointer font-normal">
                  Choose Existing
                </Label>
              </div>
            </RadioGroup>

            {accountMode === 'create' ? (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="account-name">Account Name *</Label>
                  <Input
                    id="account-name"
                    value={accountName}
                    onChange={(e) => {
                      setAccountName(e.target.value)
                      clearError('accountName')
                    }}
                    placeholder="Enter account name"
                    aria-required="true"
                    aria-invalid={!!errors.accountName}
                    aria-describedby={errors.accountName ? 'account-name-error' : undefined}
                  />
                  {errors.accountName && (
                    <p
                      id="account-name-error"
                      className="text-sm text-destructive mt-1"
                      role="alert"
                    >
                      {errors.accountName}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="account-phone">Phone *</Label>
                  <Input
                    id="account-phone"
                    value={accountPhone}
                    onChange={(e) => {
                      setAccountPhone(e.target.value)
                      clearError('accountPhone')
                    }}
                    placeholder="Enter phone number"
                    aria-required="true"
                    aria-invalid={!!errors.accountPhone}
                    aria-describedby={errors.accountPhone ? 'account-phone-error' : undefined}
                  />
                  {errors.accountPhone && (
                    <p
                      id="account-phone-error"
                      className="text-sm text-destructive mt-1"
                      role="alert"
                    >
                      {errors.accountPhone}
                    </p>
                  )}
                </div>

                <div>
                  <AddressAutocomplete
                    id="account-billing-address"
                    label="Billing Street *"
                    value={accountBillingStreet}
                    onAddressSelect={(address: AddressDetails) => {
                      setAccountBillingStreet(address.street)
                      setAccountBillingCity(address.city)
                      setAccountBillingState(address.state)
                      setAccountBillingPostalCode(address.postal_code)
                      setAccountBillingCountry(address.country)
                      clearError('accountBillingStreet')
                      clearError('accountBillingCity')
                      clearError('accountBillingState')
                      clearError('accountBillingPostalCode')
                      clearError('accountBillingCountry')
                    }}
                    onStreetChange={(street) => {
                      setAccountBillingStreet(street)
                      clearError('accountBillingStreet')
                    }}
                    placeholder="Start typing address..."
                  />
                  {errors.accountBillingStreet && (
                    <p
                      id="account-street-error"
                      className="text-sm text-destructive mt-1"
                      role="alert"
                    >
                      {errors.accountBillingStreet}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="account-billing-city">City *</Label>
                    <Input
                      id="account-billing-city"
                      value={accountBillingCity}
                      onChange={(e) => {
                        setAccountBillingCity(e.target.value)
                        clearError('accountBillingCity')
                      }}
                      placeholder="City"
                      aria-required="true"
                      aria-invalid={!!errors.accountBillingCity}
                      aria-describedby={
                        errors.accountBillingCity ? 'account-city-error' : undefined
                      }
                    />
                    {errors.accountBillingCity && (
                      <p
                        id="account-city-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.accountBillingCity}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="account-billing-state">State/Province *</Label>
                    <Input
                      id="account-billing-state"
                      value={accountBillingState}
                      onChange={(e) => {
                        setAccountBillingState(e.target.value)
                        clearError('accountBillingState')
                      }}
                      placeholder="State"
                      aria-required="true"
                      aria-invalid={!!errors.accountBillingState}
                      aria-describedby={
                        errors.accountBillingState ? 'account-state-error' : undefined
                      }
                    />
                    {errors.accountBillingState && (
                      <p
                        id="account-state-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.accountBillingState}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="account-billing-postal">Postal Code *</Label>
                    <Input
                      id="account-billing-postal"
                      value={accountBillingPostalCode}
                      onChange={(e) => {
                        setAccountBillingPostalCode(e.target.value)
                        clearError('accountBillingPostalCode')
                      }}
                      placeholder="Postal Code"
                      aria-required="true"
                      aria-invalid={!!errors.accountBillingPostalCode}
                      aria-describedby={
                        errors.accountBillingPostalCode ? 'account-postal-error' : undefined
                      }
                    />
                    {errors.accountBillingPostalCode && (
                      <p
                        id="account-postal-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.accountBillingPostalCode}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="account-billing-country">Country *</Label>
                    <Input
                      id="account-billing-country"
                      value={accountBillingCountry}
                      onChange={(e) => {
                        setAccountBillingCountry(e.target.value)
                        clearError('accountBillingCountry')
                      }}
                      placeholder="Country"
                      aria-required="true"
                      aria-invalid={!!errors.accountBillingCountry}
                      aria-describedby={
                        errors.accountBillingCountry ? 'account-country-error' : undefined
                      }
                    />
                    {errors.accountBillingCountry && (
                      <p
                        id="account-country-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.accountBillingCountry}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="account-select">Select Existing Account *</Label>
                <RecordCombobox
                  id="account-select"
                  records={accounts}
                  value={accountId}
                  onValueChange={(val) => {
                    setAccountId(val)
                    clearError('accountId')
                  }}
                  placeholder="Search accounts..."
                  searchPlaceholder="Search accounts..."
                  emptyMessage="No accounts found."
                  displayField={(a) => a.name || a.account_name || ''}
                  aria-label="Select existing account"
                  onSearchChange={setAccountSearch}
                  isLoading={accountsLoading}
                  totalCount={accountsData?.total}
                />
                {errors.accountId && (
                  <p
                    id="account-select-error"
                    className="text-sm text-destructive mt-1"
                    role="alert"
                  >
                    {errors.accountId}
                  </p>
                )}
              </div>
            )}
          </fieldset>

          {/* Contact Section */}
          <fieldset className="space-y-4 border-t pt-4">
            <legend className="font-semibold text-base">Contact</legend>

            {/* Match detection alert */}
            {contactMode === 'create' && firstMatchedContact && (
              <Alert variant="warning" aria-live="polite">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>Existing Contact Found</AlertTitle>
                <AlertDescription>
                  An existing contact &ldquo;{firstMatchedContact.first_name}{' '}
                  {firstMatchedContact.last_name}&rdquo;
                  {firstMatchedContact.email && ` (${firstMatchedContact.email})`} was found.{' '}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-sm"
                    onClick={() => {
                      setContactMode('existing')
                      setContactId(firstMatchedContact.id)
                    }}
                  >
                    Use this contact
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <RadioGroup
              value={contactMode}
              onValueChange={(val) => {
                setContactMode(val as 'create' | 'existing')
                clearError('contactId')
              }}
              aria-label="Contact creation mode"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="create" id="contact-create" />
                <Label htmlFor="contact-create" className="cursor-pointer font-normal">
                  Create New
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="existing" id="contact-existing" />
                <Label htmlFor="contact-existing" className="cursor-pointer font-normal">
                  Choose Existing
                </Label>
              </div>
            </RadioGroup>

            {contactMode === 'create' ? (
              <div className="bg-muted/50 p-3 rounded-md text-sm">
                <p>
                  Will create:{' '}
                  <span className="font-medium">
                    {lead.first_name} {lead.last_name}
                  </span>
                  {lead.email && <span className="text-muted-foreground"> ({lead.email})</span>}
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="contact-select">Select Existing Contact *</Label>
                <RecordCombobox
                  id="contact-select"
                  records={matchedContacts.length > 0 ? matchedContacts : []}
                  value={contactId}
                  onValueChange={(val) => {
                    setContactId(val)
                    clearError('contactId')
                  }}
                  placeholder="Search contacts..."
                  searchPlaceholder="Search contacts..."
                  emptyMessage="No contacts found."
                  displayField={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim()}
                  aria-label="Select existing contact"
                  totalCount={matchedContactsData?.total}
                />
                {errors.contactId && (
                  <p
                    id="contact-select-error"
                    className="text-sm text-destructive mt-1"
                    role="alert"
                  >
                    {errors.contactId}
                  </p>
                )}
              </div>
            )}
          </fieldset>

          {/* Opportunity Section */}
          <fieldset className="space-y-4 border-t pt-4">
            <legend className="font-semibold text-base">Opportunity</legend>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="skip-opportunity"
                checked={skipOpportunity}
                onCheckedChange={(checked) => setSkipOpportunity(checked === true)}
                aria-label="Don't create an opportunity upon conversion"
              />
              <Label htmlFor="skip-opportunity" className="cursor-pointer font-normal">
                Don&apos;t create an opportunity upon conversion
              </Label>
            </div>

            {!skipOpportunity && (
              <>
                <RadioGroup
                  value={opportunityMode === 'skip' ? 'create' : opportunityMode}
                  onValueChange={(val) => {
                    setOpportunityMode(val as 'create' | 'existing')
                    clearError('opportunityId')
                    clearError('opportunityName')
                    clearError('opportunityCloseDate')
                    clearError('opportunityAmount')
                  }}
                  aria-label="Opportunity creation mode"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="create" id="opportunity-create" />
                    <Label htmlFor="opportunity-create" className="cursor-pointer font-normal">
                      Create New
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="existing" id="opportunity-existing" />
                    <Label htmlFor="opportunity-existing" className="cursor-pointer font-normal">
                      Choose Existing
                    </Label>
                  </div>
                </RadioGroup>

                {opportunityMode === 'create' && (
                  <div className="space-y-4 pl-6 border-l-2">
                    <div>
                      <Label htmlFor="opportunity-name">Opportunity Name *</Label>
                      <Input
                        id="opportunity-name"
                        value={opportunityName}
                        onChange={(e) => {
                          setOpportunityName(e.target.value)
                          clearError('opportunityName')
                        }}
                        placeholder="Enter opportunity name"
                        aria-required="true"
                        aria-invalid={!!errors.opportunityName}
                        aria-describedby={
                          errors.opportunityName ? 'opportunity-name-error' : undefined
                        }
                      />
                      {errors.opportunityName && (
                        <p
                          id="opportunity-name-error"
                          className="text-sm text-destructive mt-1"
                          role="alert"
                        >
                          {errors.opportunityName}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="opportunity-amount">Amount</Label>
                      <Input
                        id="opportunity-amount"
                        type="text"
                        value={opportunityAmount}
                        onChange={(e) => {
                          setOpportunityAmount(e.target.value)
                          clearError('opportunityAmount')
                        }}
                        placeholder="0.00"
                        aria-invalid={!!errors.opportunityAmount}
                        aria-describedby={
                          errors.opportunityAmount ? 'opportunity-amount-error' : undefined
                        }
                      />
                      {errors.opportunityAmount && (
                        <p
                          id="opportunity-amount-error"
                          className="text-sm text-destructive mt-1"
                          role="alert"
                        >
                          {errors.opportunityAmount}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="opportunity-close-date">Close Date *</Label>
                      <DatePicker
                        id="opportunity-close-date"
                        value={opportunityCloseDate}
                        onChange={(v) => {
                          setOpportunityCloseDate(v ?? '')
                          clearError('opportunityCloseDate')
                        }}
                        aria-required="true"
                        aria-invalid={!!errors.opportunityCloseDate}
                        aria-describedby={
                          errors.opportunityCloseDate ? 'opportunity-close-date-error' : undefined
                        }
                      />
                      {errors.opportunityCloseDate && (
                        <p
                          id="opportunity-close-date-error"
                          className="text-sm text-destructive mt-1"
                          role="alert"
                        >
                          {errors.opportunityCloseDate}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="opportunity-stage">Stage</Label>
                      <Select value={opportunityStage} onValueChange={setOpportunityStage}>
                        <SelectTrigger id="opportunity-stage">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {opportunityStages.map((stage) => (
                            <SelectItem key={stage} value={stage}>
                              {stage}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {opportunityMode === 'existing' && (
                  <div>
                    <Label htmlFor="opportunity-select">Select Existing Opportunity *</Label>
                    <RecordCombobox
                      id="opportunity-select"
                      records={opportunities}
                      value={opportunityId}
                      onValueChange={(val) => {
                        setOpportunityId(val)
                        clearError('opportunityId')
                      }}
                      placeholder="Search opportunities..."
                      searchPlaceholder="Search opportunities..."
                      emptyMessage="No opportunities found."
                      displayField={(o) => o.name || o.opportunity_name || ''}
                      aria-label="Select existing opportunity"
                      onSearchChange={setOpportunitySearch}
                      isLoading={opportunitiesLoading}
                      totalCount={opportunitiesData?.total}
                    />
                    {errors.opportunityId && (
                      <p
                        id="opportunity-select-error"
                        className="text-sm text-destructive mt-1"
                        role="alert"
                      >
                        {errors.opportunityId}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </fieldset>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={convertMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={convertMutation.isPending}>
              {convertMutation.isPending ? 'Converting...' : 'Convert'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
